import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import useUiStore from '../store/uiStore';
import { useTranslation } from 'react-i18next';
import { enrollmentService, authService } from '../services/api';

export function resolveNotificationTarget(notification, user) {
  const target = notification?.target_url || '';
  const enrollmentId = notification?.data?.enrollment_id;
  if (!enrollmentId) return target;

  const isTeacher = user?.roles?.some((role) => {
    const roleName = typeof role === 'string' ? role : role?.name;
    return ['teacher', 'administrator'].includes(String(roleName || '').toLowerCase());
  });
  const verb = String(notification?.verb || '').toLowerCase();
  const moduleMatch = target.match(/^\/modules\/(\d+)/);
  if (isTeacher && moduleMatch && verb.includes('new enrollment request')) {
    return `/modules/${moduleMatch[1]}?tab=students&enrollment=${enrollmentId}`;
  }
  if (!isTeacher && /(approved|rejected|expired)/.test(verb)) return `/enrollments?enrollment=${enrollmentId}`;
  return target;
}

function TopNav({ title = '', subtitle = '' }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuthStore();
  const toggleMobileSidebar = useUiStore(state => state.toggleMobileSidebar);
  const [unread, setUnread] = useState(0);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const fetchUnread = () => {
      enrollmentService.getUnreadNotificationsCount().then(res => {
        if (!mounted) return;
        setUnread(res.data.unread || 0);
      }).catch(() => {});
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (lng) => i18n.changeLanguage(lng);

  const toggleNotifications = async () => {
    if (!open) {
      try {
        const res = await enrollmentService.getNotifications({ page_size: 20 });
        const items = res.data.results || res.data || [];
        setList(items);
      } catch (err) {
        setList([]);
      }
    }
    setOpen(!open);
  };

  const openNotification = (n) => {
    enrollmentService.markNotificationRead(n.id).catch(() => {});
    setList(prev => prev.map(i => i.id === n.id ? { ...i, read: true } : i));
    setUnread(u => Math.max(0, u - (n.read ? 0 : 1)));
    setOpen(false);
    const target = resolveNotificationTarget(n, user);
    if (target) {
      if (target.startsWith('/')) navigate(target);
      else window.location.assign(target);
    }
  };

  const handleLogout = async () => {
    // Navigate first so the redirect feels instant - the blacklist call is
    // a background security concern, not something the user should wait on.
    logout();
    navigate('/login');
    await authService.logout();
  };

  return (
    <header className="relative bg-white z-10 pt-[env(safe-area-inset-top)] shadow-[0_1px_2px_rgba(17,12,46,0.06)]">
      {/* Brand accent line — the same primary→accent hues as the dashboard
          hero, tying the shell to the content instead of a plain gray rule. */}
      <div className="h-[3px] bg-gradient-to-r from-primary-600 via-primary-400 to-accent-400" />
      <div className="flex items-center justify-between gap-2 px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={toggleMobileSidebar}
            className="md:hidden shrink-0 p-2 -ml-1 text-gray-500 hover:bg-surface-100 rounded-lg"
            aria-label="Open menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="min-w-0">
            <h2 className="text-lg sm:text-2xl font-bold font-display tracking-tight text-gray-800 truncate">{title}</h2>
            {subtitle && <p className="hidden sm:block text-sm text-gray-500 truncate">{subtitle}</p>}
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <div className="flex items-center gap-0.5 sm:gap-1 bg-surface-100/80 rounded-full p-1">
            <select
              aria-label="Select language"
              value={i18n.language || 'en'}
              onChange={(e) => changeLanguage(e.target.value)}
              className="bg-transparent text-sm text-gray-600 rounded-full px-1.5 sm:px-2 py-1.5 max-w-[4.5rem] sm:max-w-none focus:outline-none"
            >
              <option value="en">English</option>
              <option value="fr">Français</option>
              <option value="ar">العربية</option>
            </select>

            <div className="relative" ref={dropdownRef}>
              <button onClick={toggleNotifications} className="relative p-2 text-gray-500 hover:bg-white hover:shadow-sm rounded-full transition">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unread > 0 && (
                  <span className="absolute top-0 right-0 -mt-1 -mr-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">{unread}</span>
                )}
              </button>

              {open && (
              <div className="origin-top-right rtl:origin-top-left absolute right-0 rtl:right-auto rtl:left-0 mt-2 w-80 max-w-[85vw] bg-white border border-surface-200 rounded-2xl shadow-card-hover z-50 overflow-hidden">
                <div className="p-3 border-b border-surface-100 font-display font-semibold text-gray-800">{t('Notifications')}</div>
                <div className="max-h-64 overflow-y-auto">
                  {list.length === 0 && <div className="p-4 text-sm text-gray-400 text-center">{t('No notifications')}</div>}
                  {list.map(n => (
                    <div key={n.id} className={`p-3 hover:bg-surface-50 cursor-pointer flex items-start gap-3 transition ${n.read ? 'opacity-60' : ''}`} onClick={() => openNotification(n)}>
                      {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-1.5 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800">{n.verb}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-2 border-t border-surface-100 text-center">
                  <button onClick={() => window.location.href = '/enrollments'} className="text-sm text-primary-600 hover:text-primary-700 font-medium">{t('View all')}</button>
                </div>
              </div>
            )}
            </div>

            <button onClick={() => navigate('/profile')} title={t('Profile')} className="p-1 hover:bg-white hover:shadow-sm rounded-full transition">
              {user?.profile_picture ? (
                <img src={user.profile_picture} alt="avatar" className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover ring-2 ring-primary-100" />
              ) : (
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold ring-2 ring-primary-100">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
              )}
            </button>
          </div>

          <button onClick={handleLogout} title={t('Logout')} aria-label={t('Logout')} className="p-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
}

export default TopNav;
